import React, { memo, useCallback, useMemo } from 'react';
import { styled } from '@compiled/react';
import noop from 'lodash/noop';
import Popup, { type TriggerProps } from '@atlaskit/popup'; // ignore-for-ENGHEALTH-17759
import { xcss, Pressable } from '@atlaskit/primitives';
import type { OptionType } from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { CellWrapper } from '@atlassian/jira-business-fields/src/common/cell-wrapper/index.tsx';
import { FixItErrorFlag } from '@atlassian/jira-business-fields/src/common/ui/fixit-error-flag/index.tsx';
import LazySelect from '@atlassian/jira-business-fields/src/common/ui/lazy-select/index.tsx';
import { getPopupComponentWithHeight } from '@atlassian/jira-business-fields/src/common/ui/popup/index.tsx';
import { gridSize, borderRadius } from '@atlassian/jira-common-styles/src/main.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import { useEditField } from '@atlassian/jira-issue-field-base/src/services/edit-field-service/main.tsx';
import type { SelectOption } from '@atlassian/jira-issue-field-select/src/ui/edit/types.tsx';
import { CardPriority } from '@atlassian/jira-platform-card/src/common/ui/priority/index.tsx';
import { PRIORITY_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { DEFAULT_ROW_HEIGHT } from '../../../common/constants.tsx';
import type { PopupSelectTargetOptions } from '../../../common/ui/lazy-popup-select/types.tsx';
import { CellContentWrapper, CellContentContainer } from '../../../common/ui/list-cell/styled.tsx';
import messages from './messages.tsx';
import type { PriorityCellViewProps, PriorityCellProps, PriorityValue } from './types.tsx';
import { transformValue } from './utils.tsx';

// -gridsize coming from ImgSpan padding right
const POPUP_OFFSET: [number, number] = [-gridSize, 2 - DEFAULT_ROW_HEIGHT];

const PriorityCellView = memo<PriorityCellViewProps>(
	({ name, iconUrl, onClick, width, height, isHovering }: PriorityCellViewProps) => {
		if (name === null || name === undefined || iconUrl == null) {
			return null;
		}

		const content = <CardPriority uri={iconUrl} name={name} />;

		return (
			<CellContentContainer
				onClick={onClick}
				width={width}
				height={height}
				data-testid="business-list.ui.list-view.priority-cell.priority-cell-view"
			>
				{isHovering ? (
					<Tooltip content={name}>
						<InnerContainer>{content}</InnerContainer>
					</Tooltip>
				) : (
					content
				)}
			</CellContentContainer>
		);
	},
);
PriorityCellView.displayName = 'PriorityCellView';

const PriorityCell = memo<PriorityCellProps>(
	({
		name,
		iconUrl,
		issueKey,
		issueType,
		width,
		height,
		fieldType,
		isSelected = false,
		wrapperRef,
		isEditable,
		selectOptions,
		issueTypeId,
		fieldName,
		testId,
		editMode = false,
		setEditMode = noop,
		isHovering = false,
		inputValue,
		onInputChange,
	}: PriorityCellProps) => {
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const { formatMessage } = useIntl();

		const [{ value, error }, { saveValue }] = useEditField<PriorityValue, null>({
			fieldKey: PRIORITY_TYPE,
			issueKey,
			fieldType,
			initialValue: {
				name,
				iconUrl,
			},
		});

		const cellViewProps = useMemo(
			() => ({
				name: value && value.name,
				iconUrl: value && value.iconUrl,
				width,
				height,
			}),
			[value, width, height],
		);

		const handleChange = useCallback(
			(option: OptionType | null) => {
				setEditMode(false);
				const selectedOption = selectOptions.find(
					(fieldoption) => fieldoption.name === option?.value,
				);
				selectedOption && saveValue(selectedOption, null, createAnalyticsEvent({}));
				setTimeout(() => {
					wrapperRef?.current?.focus();
				});
			},
			[setEditMode, selectOptions, saveValue, createAnalyticsEvent, wrapperRef],
		);

		const handlePopupClose = useCallback(() => {
			setEditMode(false);
		}, [setEditMode]);

		const popupTrigger = useCallback(
			(triggerProps: TriggerProps | PopupSelectTargetOptions) => (
				<Pressable
					{...triggerProps}
					xcss={popupTriggerStyles}
					testId="business-list.ui.list-view.priority-cell.trigger"
				>
					<PriorityCellView {...cellViewProps} isHovering={isHovering} />
				</Pressable>
			),
			[isHovering, cellViewProps],
		);

		// we need to sort the options so that the selected priority is at the top of the list
		// as the first option is hovered by default and will be selected if `Enter` is pressed
		// immediately after the select dropdown is shown
		const prioritySelectOptions = useMemo(() => {
			let selectedPriorityOption: SelectOption | null = null;
			const priorityOptions: SelectOption[] = [];
			(selectOptions || []).forEach((option) => {
				if (option.name == null) {
					return;
				}
				const priorityOption = {
					label: option.name,
					value: option.name,
				};
				if (option.name === value?.name) {
					selectedPriorityOption = priorityOption;
				} else {
					priorityOptions.push(priorityOption);
				}
			});
			if (selectedPriorityOption != null) {
				priorityOptions.unshift(selectedPriorityOption);
			}
			return priorityOptions;
		}, [selectOptions, value?.name]);

		const getFormatOptionLabel = useCallback(
			(option: SelectOption | OptionType) => {
				const selectedOption = selectOptions.find(
					(fieldoption) => fieldoption.name === option?.value,
				);

				if (selectedOption?.iconUrl === undefined) {
					return <>{option.label || ''}</>;
				}

				return (
					<OptionWrapper data-testid="business-list.ui.list-view.priority-cell.priority-option">
						<ImgSpan>
							<StyledImage src={selectedOption.iconUrl} alt={option.label} role="none" />
						</ImgSpan>
						<FlexSpan>{option.label}</FlexSpan>
					</OptionWrapper>
				);
			},
			[selectOptions],
		);

		const popupContent = useCallback(
			() => (
				<CellContentWrapper
					// -1px border left from gridcell
					preferredWidth={width - 1}
					data-testid="business-list.ui.list-view.priority-cell.popup-content"
				>
					<LazySelect
						autoFocus
						menuIsOpen
						placeholder={formatMessage(messages.placeholder)}
						options={prioritySelectOptions}
						onChange={handleChange}
						value={transformValue(value)}
						formatOptionLabel={getFormatOptionLabel}
						isMulti={false}
						isInvalid={false}
						isClearable={false}
						tabSelectsValue
						inputValue={inputValue}
						onInputChange={onInputChange}
					/>
				</CellContentWrapper>
			),
			[
				formatMessage,
				getFormatOptionLabel,
				handleChange,
				inputValue,
				onInputChange,
				prioritySelectOptions,
				value,
				width,
			],
		);

		const popupComponent = useMemo(
			() => getPopupComponentWithHeight({ numOptions: selectOptions.length ?? 0 }),
			[selectOptions.length],
		);

		const popup = useMemo(
			() => (
				<Popup
					isOpen={editMode}
					placement="bottom-start"
					onClose={handlePopupClose}
					trigger={popupTrigger}
					offset={POPUP_OFFSET}
					popupComponent={popupComponent}
					content={popupContent}
					autoFocus={false}
				/>
			),
			[editMode, handlePopupClose, popupTrigger, popupContent, popupComponent],
		);

		const content = useMemo(
			() =>
				isHovering || editMode || isSelected ? (
					popup
				) : (
					<PriorityCellView {...cellViewProps} isHovering={isHovering} />
				),
			[isHovering, editMode, isSelected, popup, cellViewProps],
		);

		const cellContent = (
			<>
				{isEditable === false ? (
					<PriorityCellView {...cellViewProps} isHovering={isHovering} />
				) : (
					content
				)}
				{error && (
					<FixItErrorFlag
						error={error}
						title={
							expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
								? messages.errorTitleIssueTermRefresh
								: messages.errorTitle
						}
						description={messages.errorMessage}
						issueType={issueType}
						issueTypeId={issueTypeId}
						fieldId={PRIORITY_TYPE}
						fieldName={fieldName}
					/>
				)}
			</>
		);

		return (
			<CellWrapper isEditable={isEditable} testId={testId}>
				{cellContent}
			</CellWrapper>
		);
	},
);

export default PriorityCell;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const InnerContainer = styled.div({
	display: 'flex',
	height: '100%',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FlexSpan = styled.span({
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ImgSpan = styled.span({
	display: 'flex',
	paddingRight: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OptionWrapper = styled.span({
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${borderRadius}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledImage = styled.img({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 2}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 2}px`,
});

const popupTriggerStyles = xcss({
	backgroundColor: 'color.background.neutral.subtle',
	padding: 'space.0',
});
